import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { MOBILE, SHRINKABLE } from '../../responsive';

/**
 * A centred modal for a focused edit surface.
 *
 * Michael, 2026-09-17: "I don't like the side bar popping up when selecting a time
 * entry to edit, approve, delete, etc... Can you make it a modal that expands from the
 * center of the screen instead?"
 *
 * DELIBERATELY PROP-COMPATIBLE WITH {@link SideBlade} — same `open` / `onClose` /
 * `title` / `children` / `size` contract — so a surface moves between the two by
 * changing one import. Only the timecard is switched today; Employees and Department
 * Mapping still use the blade, and can follow whenever Michael wants them to rather
 * than being changed underneath him as a side effect of this request.
 */

const useStyles = makeStyles({
  surface: {
    // Sized to the content, not to a fixed drawer width, and capped so a long form
    // scrolls inside the modal rather than growing past the viewport.
    maxWidth: 'min(560px, calc(100vw - 32px))',
    maxHeight: 'calc(100vh - 64px)',
    [MOBILE]: {
      // A phone has no room for a centred card with margins on both sides; below the
      // breakpoint it takes the screen, the same concession SideBlade makes.
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: 'calc(100vh - 32px)',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    overflowY: 'auto',
    ...SHRINKABLE,
  },
});

/** Width preset, kept for prop-compatibility with SideBlade. */
export type CenterModalSize = 'small' | 'medium' | 'large';

export interface CenterModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: CenterModalSize;
}

export function CenterModal({ open, onClose, title, children }: CenterModalProps): JSX.Element {
  const styles = useStyles();

  return (
    <Dialog
      open={open}
      // `modal` rather than `alert`: Esc and a backdrop click should dismiss, which is
      // what the blade did and what people already expect from this surface.
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          {/*
           * 🔥 MUST BE DialogContent, NOT a plain div. DialogBody is a THREE-COLUMN
           * grid: DialogTitle spans 1/4 and DialogActions spans 2/4, but an unknown
           * child gets `grid-column: auto` and lands in a single ~247px column — half
           * the surface. The form then overflowed sideways, clipping the buttons and
           * putting a horizontal scrollbar inside the modal. DialogContent carries the
           * span Fluent's grid expects.
           */}
          <DialogContent className={styles.body}>{children}</DialogContent>
          <DialogActions>
            <Button appearance="subtle" aria-label="Close" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
